import { FleamarketArticleStatus } from "__generated__/jampot/alpha-ca";
import { Button, Flex, Text } from "@daangn/carotene";
import { atoms } from "@daangn/carotene/css";
import { KarrotLocalCountryCode } from "@karrotmarket-com/core";
import clsx from "clsx";

import useDetectUserAgent from "~/hooks/useDetectUserAgent";
import { useDisclosure } from "~/hooks/useDisclosure";
import { useLocale, useRootLoaderData } from "~/remix-lib";

import { GL_APPSFLYER_LINK } from "../constants";
import * as css from "./DownloadAppButton.css";
import Portal from "./Portal";
import GlobalQrModal, { KRQrModal } from "./QrModal";
import { useKRAppScheme } from "./useKRAppScheme";

type DownloadAppButtonProps = {
  publicId: string;
  className?: string;
  status?: FleamarketArticleStatus;
  onClick?: () => void;
};

export default function DownloadAppButton({
  publicId,
  className,
  status = FleamarketArticleStatus.Ongoing,
  onClick,
}: DownloadAppButtonProps) {
  const { t } = useLocale();

  const rootData = useRootLoaderData();
  const userAgent = useDetectUserAgent();
  const { isOpen, onOpen, setOpenState } = useDisclosure();

  const handleClick = () => {
    onClick?.();

    if (userAgent.isMobile) {
      window.open(GL_APPSFLYER_LINK.Mobile.ArticleDetail(publicId));
      return;
    }

    onOpen();
  };

  return (
    <>
      <div className={clsx(css.desktopButton, className)}>
        <Button
          variant="brand"
          size="large"
          onClick={handleClick}
          className={atoms({
            width: "full",
          })}
        >
          {t(
            `DownloadAppButton.${
              status === FleamarketArticleStatus.Closed ? "sold_label" : "label"
            }`,
          )}
        </Button>
        {rootData.karrotLocalCountryCode === KarrotLocalCountryCode.JP ? (
          <JpDescription />
        ) : null}
      </div>
      <Portal blockBodyScroll={false}>
        <div className={clsx(css.fixedMobileButton, className)}>
          <Button
            variant="brand"
            size="xlarge"
            onClick={handleClick}
            className={atoms({
              width: "full",
            })}
          >
            {t(
              `DownloadAppButton.${
                status === FleamarketArticleStatus.Closed
                  ? "sold_label"
                  : "label"
              }`,
            )}
          </Button>
          {rootData.karrotLocalCountryCode === KarrotLocalCountryCode.JP ? (
            <JpDescription />
          ) : null}
        </div>
      </Portal>
      <GlobalQrModal
        open={isOpen}
        onOpenChange={setOpenState}
        qrLink={GL_APPSFLYER_LINK.Web.ArticleDetail(publicId)}
      />
    </>
  );
}

type Props = {
  target: string;
  label?: string;
  className?: string;
  onClick?: () => void;
  size?: {
    desktop?: "large" | "medium";
  };
};
export function KRDownloadAppButton({
  target,
  label = "당근 앱에서 보기",
  className,
  size = {
    desktop: "large",
  },
  onClick,
}: Props) {
  const { isOpen, onOpen, setOpenState } = useDisclosure();
  const userAgent = useDetectUserAgent();
  const { getQrLink, openApp } = useKRAppScheme();

  const handleClick = () => {
    onClick?.();

    if (userAgent.isMobile) {
      openApp(target);
      return;
    }

    onOpen();
  };

  return (
    <>
      <div className={clsx(css.desktopButton, className)}>
        <Button
          variant="brand"
          size={size.desktop}
          onClick={handleClick}
          className={atoms({
            width: "full",
          })}
        >
          {label}
        </Button>
      </div>
      <Portal blockBodyScroll={false}>
        <div className={clsx(css.fixedMobileButton, className)}>
          <Button
            variant="brand"
            size="xlarge"
            onClick={handleClick}
            className={atoms({
              width: "full",
            })}
          >
            {label}
          </Button>
        </div>
      </Portal>
      <KRQrModal
        open={isOpen}
        onOpenChange={(o) => setOpenState(o)}
        qrLink={getQrLink(target)}
      />
    </>
  );
}

function JpDescription() {
  return (
    <Flex
      justifyContent="center"
      width="full"
      paddingTop={2.5}
      className={css.jpDescriptionWrapper}
    >
      <Text size="small" color="neutral" className={css.jpDescription}>
        取引はアプリでのみ可能です。
        <br />
        ダウンロードしてご利用ください。
      </Text>
    </Flex>
  );
}
